import { useState, useCallback } from 'react'
import { useDebounce } from '@/utilities/useDebounce'
import { BaseFormSelect, BaseFormSelectProps } from './FormSelect'
/**
 * FormSelectWithSearch
 *
 * A wrapper around BaseFormSelect that implements a custom, buffer-based search.
 * This component explicitly overrides Radix UI's default keyboard navigation to
 * provide consistent multi-character matching and numeric input handling across
 * both open and closed states.
 *
 */
const DEBOUNCE_TIME_IN_MS = 500 // Idle time in milliseconds before the search search buffer is reset
export function FormSelectWithSearch(props: BaseFormSelectProps) {
  const { options, value, onChange, onOpenChange } = props
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, DEBOUNCE_TIME_IN_MS)

  // Clear the search buffer synchronously once the debounce period fires.
  // Use a ref to track the previous debouncedSearch so we only clear when it transitions
  // from a non-empty string to a new value — avoiding the set-state-in-effect anti-pattern.
  const [prevDebounced, setPrevDebounced] = useState(debouncedSearch)
  if (prevDebounced !== debouncedSearch) {
    setPrevDebounced(debouncedSearch)
    if (debouncedSearch !== '' && search !== '') {
      setSearch('')
    }
  }

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      // Only handle printable characters (single length strings)
      if (event.key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) return

      /**
       * CRITICAL: Overriding Radix UI Default search
       *
       * We use event.preventDefault() and event.stopPropagation() here to suppress
       * Radix UI's internal search logic. Radix's default behavior often
       * conflicts with custom buffer-based matching, especially for numeric inputs
       * (e.g., jumping between options instead of accumulating digits).
       *
       * By intercepting here in the capture phase, we ensure our custom
       * accumulation and cycling logic takes full precedence.
       */
      event.preventDefault()
      event.stopPropagation()

      const char = event.key.toLowerCase()
      const newSearch = search + char

      // 1. Try matching with accumulated string
      let matches = options.filter((o) => o.label.toLowerCase().includes(newSearch))

      if (matches.length > 0) {
        setSearch(newSearch)
        const match = matches[0]
        if (match && match.value !== value) {
          onChange(match.value)
        }
      } else {
        // 2. Fallback to fresh single-char match
        matches = options.filter((o) => o.label.toLowerCase().includes(char))
        if (matches.length > 0) {
          setSearch(char)
          const match = matches[0]
          if (match && match.value !== value) {
            onChange(match.value)
          }
        }
      }
    },
    [search, options, value, onChange],
  )

  const handleOpenChange = useCallback(
    (open: boolean) => {
      // Clear any accumulated search buffer when the dropdown closes.
      if (!open) {
        setSearch('')
      }
      onOpenChange?.(open)
    },
    [onOpenChange],
  )

  return (
    <div onKeyDownCapture={handleKeyDown}>
      <BaseFormSelect {...props} onOpenChange={handleOpenChange} />
    </div>
  )
}
